// Feature: features/nma-appeal-templates-demo-ui.md
const TEMPLATES_STORAGE_KEY = 'nma_appeal_templates_v1';
const CLAIM_SELECTIONS_STORAGE_KEY = 'nma_claim_appeal_template_selections_v1';

export const PAYER_TEMPLATE_OPTIONS = [
  'All Payers',
  'CIGNA',
  'AETNA',
  'UNITED HEALTHCARE / UHC',
  'BCBS',
  'MEDICARE / MA',
  'HUMANA',
  'UMR',
  'OTHER / COMMERCIAL',
];

export const PROCEDURE_TYPE_OPTIONS = [
  'All Procedures',
  'Cervical Spine (ACDF / Posterior)',
  'Thoracic / Lumbar Spine (TLIF / ALIF / Decompression)',
  'Scoliosis / Spinal Deformity',
  'Craniotomy / Intracranial',
  'ENT / Thyroid / Parotid',
  'Vascular / Carotid (CEA / TCAR / Aortic)',
];

export const DENIAL_SCENARIO_OPTIONS = [
  'All Scenarios',
  'Medical Necessity / Experimental',
  'CPT-DX Crosswalk (Policy 0509)',
  'Repeat Baseline (-76)',
  'EEG Modality (95822 vs 95955)',
  'Post-Record Review / Clinical Doc',
  'Corrected Claim (Freq Code 7)',
];

export const SUBMISSION_CHANNEL_OPTIONS = [
  {
    value: 'STANDARD_APPEAL_PACKET',
    label: 'Formal Clinical Appeal Letter + PDF Packet',
    badge: 'Formal Appeal PDF',
    color: 'blue',
  },
  {
    value: 'PAYER_PORTAL_EAPPEAL',
    label: 'Payer Portal eAppeal (Availity / NaviNet / UHC Link)',
    badge: 'Portal eAppeal',
    color: 'purple',
  },
  {
    value: 'CORRECTED_CLAIM_FREQ_7',
    label: 'Corrected Claim (Freq Code 7) + Reconsideration Note',
    badge: 'Freq Code 7 Rebill',
    color: 'teal',
  },
];

export const FILE_ROLE_OPTIONS = [
  'Winning Appeal Sample',
  'Payer Medical Policy',
  'Clinical Guideline / Literature',
  'Payer Appeal Cover Form',
  'Case Supplement',
];

export const TEMPLATE_PLACEHOLDERS = [
  { token: '{{CLAIM_NUMBER}}', label: 'Claim #' },
  { token: '{{CASE_NUMBER}}', label: 'NMA Case #' },
  { token: '{{PATIENT_NAME}}', label: 'Patient Name' },
  { token: '{{DOS}}', label: 'Date of Service' },
  { token: '{{PAYER_NAME}}', label: 'Payer Name' },
  { token: '{{ICN}}', label: 'Payer ICN' },
  { token: '{{FACILITY_NAME}}', label: 'Facility' },
  { token: '{{RENDERING_PROVIDER}}', label: 'Monitoring MD' },
  { token: '{{POS}}', label: 'POS' },
  { token: '{{PROCEDURE_TYPE}}', label: 'Procedure Type' },
  { token: '{{DENIED_CPTS}}', label: 'Denied CPTs' },
  { token: '{{PAID_CPTS}}', label: 'Paid CPTs' },
  { token: '{{TOP_CARC}}', label: 'CARC / RARC' },
  { token: '{{PRIMARY_DX}}', label: 'Primary ICD-10' },
  { token: '{{ALL_DX}}', label: 'All ICD-10s' },
  { token: '{{DENIAL_REASON}}', label: 'Denial Reason' },
  { token: '{{SELECTED_POLICY_CITATIONS}}', label: 'Selected Payer Policies' },
];

export const DEFAULT_APPEAL_TEMPLATES = [
  {
    id: 'tpl-nma-base-structure',
    name: 'Universal NMA Clinical Appeal Header & Structure',
    description:
      'Standard NMA letterhead structure, claim/ICN identification block, operative correlation table, and enclosure checklist included on all formal appeals.',
    payer_match: 'All Payers',
    procedure_type: 'All Procedures',
    denial_scenario: 'All Scenarios',
    claim_type: 'both',
    cpt_codes: [],
    is_base_template: true,
    is_active: true,
    updated_at: '2026-10-25',
    template_text: `RE: Formal Clinical Appeal — Claim {{CLAIM_NUMBER}} (NMA Case #{{CASE_NUMBER}})
Patient: {{PATIENT_NAME}} | Date of Service: {{DOS}} | Payer ICN: {{ICN}}
Payer: {{PAYER_NAME}} | Facility: {{FACILITY_NAME}} (POS {{POS}})
Monitoring Neurophysiologist: {{RENDERING_PROVIDER}}
Procedure Category: {{PROCEDURE_TYPE}}
Disputed CPT Code(s): {{DENIED_CPTS}} | Remittance Code(s): {{TOP_CARC}}

Required Letter Sections:
1. Executive Coverage & Medical Necessity Summary citing primary diagnosis {{PRIMARY_DX}} (Full DX sequence: {{ALL_DX}}).
2. Modality-by-Modality Clinical Correlation tying each denied CPT ({{DENIED_CPTS}}) to real-time neurophysiologic risk documented in the Operative Report and signed IONM Summary Report.
3. Direct Rebuttal of Payer Denial Reason ("{{DENIAL_REASON}}") with verbatim policy & society guideline citations.
4. Enclosures Checklist: Operative Report, Signed IONM Professional Summary Report, Tech Log/Timestamps, and Hospital Face Sheet.`,
    attached_files: [
      {
        id: 'file-base-1',
        name: 'NMA_Standard_Appeal_Cover_Letterhead_2026.pdf',
        size_kb: 148,
        role: 'Payer Appeal Cover Form',
        notes: 'Standard NMA corporate letterhead, W-9 / NPI block, and ERISA / NSA rights reservation.',
      },
      {
        id: 'file-base-2',
        name: 'AAN_ASNM_Multimodal_IONM_Evidence_Statement.pdf',
        size_kb: 612,
        role: 'Clinical Guideline / Literature',
        notes: 'American Academy of Neurology (AAN) & ASNM evidence update supporting multimodal SSEP + TcMEP + EMG.',
      },
    ],
  },
  {
    id: 'tpl-cigna-0509-spine',
    name: 'Cigna Policy 0509 — Spine IONM Medical Necessity & High-Acuity DX Override',
    description:
      'Rebuts Cigna CO-16/M76 & CO-167/MA63 denials under Cigna Medical Coverage Policy 0509 by highlighting high-acuity spinal cord risk, myelopathy, spondylolisthesis, deformity, or revision hardware.',
    payer_match: 'CIGNA',
    procedure_type: 'Thoracic / Lumbar Spine (TLIF / ALIF / Decompression)',
    denial_scenario: 'CPT-DX Crosswalk (Policy 0509)',
    claim_type: 'pro',
    cpt_codes: ['95938', '95939', '95940', '95941', '95861', '95870'],
    is_base_template: false,
    is_active: true,
    updated_at: '2026-10-26',
    template_text: `CIGNA POLICY 0509 CLINICAL APPEAL STRATEGY (Claim {{CLAIM_NUMBER}}):
- Target Denial: Cigna Medical Coverage Policy 0509 ("Intraoperative Neurophysiologic Monitoring") / CARC {{TOP_CARC}} ("{{DENIAL_REASON}}").
- Clinical Exception Argument: While Cigna Policy 0509 restricts routine single-level decompression, it explicitly covers SSEP (95938/95925/95926) and TcMEP (95939) when surgical manipulation places the spinal cord or nerve roots at elevated risk due to myelopathy, spondylolisthesis/instability, spinal cord compression, deformity correction, or instrumentation across critical neural structures.
- Diagnosis & Operative Proof: Point directly to diagnosis sequence {{ALL_DX}} (Primary: {{PRIMARY_DX}}) and quote the surgeon's Operative Report at {{FACILITY_NAME}} confirming multi-modality monitoring was ordered prior to incision to prevent permanent paraparesis or radicular motor deficit.
- Companion Line Consistency: Note that companion IONM modalities ({{PAID_CPTS}}) were adjudicated on this same surgical encounter.`,
    attached_files: [
      {
        id: 'file-cigna-1',
        name: 'Cigna_Medical_Coverage_Policy_0509_IONM_Annotated.pdf',
        size_kb: 890,
        role: 'Payer Medical Policy',
        notes: 'Highlighted covered indication table and high-acuity ICD-10 crosswalk exceptions from Cigna MCP 0509.',
      },
      {
        id: 'file-cigna-2',
        name: 'Cigna_Winning_Spine_Appeal_Sample_Redacted.docx',
        size_kb: 94,
        role: 'Winning Appeal Sample',
        notes: 'Proven overturn template for Cigna CO-16/M76 & Policy 0509 spine claims.',
      },
    ],
  },
  {
    id: 'tpl-cigna-cervical-spine',
    name: 'Cigna Policy 0509 — Cervical Spine (ACDF / Posterior Myelopathy) Template',
    description:
      'Specialized Cigna appeal template for Cervical Spine surgeries (ACDF, corpectomy, posterior cervical fusion) emphasizing spinal cord compression, myelopathy, and four-limb SSEP/MEP (95938/95939).',
    payer_match: 'CIGNA',
    procedure_type: 'Cervical Spine (ACDF / Posterior)',
    denial_scenario: 'CPT-DX Crosswalk (Policy 0509)',
    claim_type: 'pro',
    cpt_codes: ['95938', '95939', '95941', '95860', '95861'],
    is_base_template: false,
    is_active: true,
    updated_at: '2026-10-26',
    template_text: `CIGNA CERVICAL SPINE CORD-RISK APPEAL (Claim {{CLAIM_NUMBER}}):
- Procedure Context: {{PROCEDURE_TYPE}} performed at {{FACILITY_NAME}} on {{DOS}} with diagnoses {{ALL_DX}}.
- Cervical Cord Vulnerability: Emphasize that cervical spine decompression/instrumentation directly adjacent to the cervical spinal cord carries catastrophic risk of C5 palsy and quadriplegia during distraction, graft insertion, and positioning.
- Four-Limb SSEP (95938) & MEP (95939) Justification: Upper-extremity signals monitor brachial plexus/C5-T1 root integrity and positioning ischemia, while lower-extremity signals monitor long-tract corticospinal and dorsal column conduction across the cervical surgical level.`,
    attached_files: [
      {
        id: 'file-cigna-cerv-1',
        name: 'Cigna_MCP_0509_Cervical_Myelopathy_Criteria.pdf',
        size_kb: 540,
        role: 'Payer Medical Policy',
        notes: 'Cigna Policy 0509 cervical cord compression and anterior/posterior instrumentation coverage section.',
      },
    ],
  },
  {
    id: 'tpl-aetna-cpb-0697',
    name: 'Aetna CPB 0697 — Clinical Neurophysiologic Monitoring & NSA Inpatient Defense',
    description:
      'Addresses Aetna PI-55/N623 (Experimental/Investigational) and CO-50 denials citing Aetna Clinical Policy Bulletin (CPB) 0697 and Federal No Surprises Act (NSA) protections for facility encounters.',
    payer_match: 'AETNA',
    procedure_type: 'All Procedures',
    denial_scenario: 'Medical Necessity / Experimental',
    claim_type: 'pro',
    cpt_codes: ['95938', '95939', '95861', '95870', '95955', '95822', '95940', '95941'],
    is_base_template: false,
    is_active: true,
    updated_at: '2026-10-24',
    template_text: `AETNA CPB 0697 & NSA RECONSIDERATION STRATEGY (Claim {{CLAIM_NUMBER}}):
- Target Denial: Aetna Clinical Policy Bulletin (CPB) 0697 / Remittance {{TOP_CARC}} ("{{DENIAL_REASON}}") on CPT(s) {{DENIED_CPTS}}.
- CPB 0697 Coverage Alignment: Demonstrate how the patient's surgical pathology ({{ALL_DX}}) and procedure ({{PROCEDURE_TYPE}}) satisfy Aetna CPB 0697 medically necessary criteria for intraoperative SSEP, TcMEP, and EMG monitoring due to risk of iatrogenic spinal cord, cranial nerve, or cerebral cortex injury.
- Facility POS {{POS}} & No Surprises Act (NSA) Notice: Patient received care at an participating facility ({{FACILITY_NAME}}, POS {{POS}}) where ancillary intraoperative neurophysiologic monitoring was ordered by the operating surgeon without patient choice of ancillary provider.`,
    attached_files: [
      {
        id: 'file-aetna-1',
        name: 'Aetna_CPB_0697_IONM_Policy_Excerpt.pdf',
        size_kb: 1120,
        role: 'Payer Medical Policy',
        notes: 'Aetna CPB 0697 covered indications for spinal, intracranial, vascular, and cranial nerve monitoring.',
      },
      {
        id: 'file-aetna-2',
        name: 'Aetna_ICMN_WPRP_Appeal_Fax_Cover_859_455_8650.pdf',
        size_kb: 115,
        role: 'Payer Appeal Cover Form',
        notes: 'Aetna ICMN / WPRP reconsideration routing sheet with NSA claim flags.',
      },
    ],
  },
  {
    id: 'tpl-uhc-optum-post-review',
    name: 'UHC / Optum — Post-Review Reconsideration & Pre-Incision Baseline (-76) Defense',
    description:
      'Designed for UnitedHealthcare / Optum / UMR post-record review denials (Code HP, Code AR, CO-150, CO-252) and pre-incision vs. post-positioning repeat baseline SSEP (95938-26-76).',
    payer_match: 'UNITED HEALTHCARE / UHC',
    procedure_type: 'All Procedures',
    denial_scenario: 'Post-Record Review / Clinical Doc',
    claim_type: 'pro',
    cpt_codes: ['95938', '95939', '95940', '95941', '95861'],
    is_base_template: false,
    is_active: true,
    updated_at: '2026-10-25',
    template_text: `UHC / OPTUM CLINICAL RECONSIDERATION STRATEGY (Claim {{CLAIM_NUMBER}}):
- Target Denial: UHC Policies 2026R5008A / 2025R0012C — Remittance {{TOP_CARC}} ("{{DENIAL_REASON}}").
- Signed Professional Report & Real-Time Supervision Proof: Cite the physician signature block and real-time chat/supervision timestamps of {{RENDERING_PROVIDER}} in the attached IONM Summary Report and Technical Log.
- Pre-Incision Prone/Positioning Baseline Defense (if 95938-76 / Code AR is disputed): Point to the exact pre-positioning supine baseline timestamp and post-flip prone baseline timestamp in the IONM log, demonstrating distinct medically necessary diagnostic baseline acquisitions prior to surgical incision to detect positioning-induced brachial/cervical neuropraxia.`,
    attached_files: [
      {
        id: 'file-uhc-1',
        name: 'UHC_Neurophysiologic_Testing_Policy_2026R5008A.pdf',
        size_kb: 780,
        role: 'Payer Medical Policy',
        notes: 'UnitedHealthcare Commercial & Medicare Advantage reimbursement policy for IONM.',
      },
      {
        id: 'file-uhc-2',
        name: 'UHC_Optum_PreIncision_Baseline_Winning_Sample.pdf',
        size_kb: 245,
        role: 'Winning Appeal Sample',
        notes: 'Sample winning reconsideration letter overturning Optum Code AR / Code HP denials.',
      },
    ],
  },
  {
    id: 'tpl-eeg-95822-95955',
    name: 'Intraoperative EEG (95822 vs. 95955) Cortical & Vascular Medical Necessity Template',
    description:
      'Defends intraoperative EEG monitoring (CPT 95822 or 95955) when denied as experimental, routine, or mismatched modality while companion SSEP/MEP/EMG lines paid.',
    payer_match: 'All Payers',
    procedure_type: 'Craniotomy / Intracranial',
    denial_scenario: 'EEG Modality (95822 vs 95955)',
    claim_type: 'pro',
    cpt_codes: ['95822', '95955'],
    is_base_template: false,
    is_active: true,
    updated_at: '2026-10-25',
    template_text: `INTRAOPERATIVE EEG (95822 / 95955) MEDICAL NECESSITY ARGUMENT (Claim {{CLAIM_NUMBER}}):
- Disputed EEG Modality: CPT {{DENIED_CPTS}} denied with {{TOP_CARC}} ("{{DENIAL_REASON}}"), while companion modalities ({{PAID_CPTS}}) were approved on this same case.
- Clinical Necessity of Continuous Multi-Channel EEG: Continuous electroencephalography was performed and interpreted in real time by {{RENDERING_PROVIDER}} to monitor cortical perfusion, depth of anesthesia / burst-suppression symmetry, and subclinical ischemia during {{PROCEDURE_TYPE}} (Diagnoses: {{ALL_DX}}).
- Why SSEP/MEP Depend on Concurrent EEG: Accurate interpretation of cortical SSEP (95938) and transcranial MEP (95939) amplitude changes requires continuous EEG correlation to rule out anesthetic fade or global cerebral hypoperfusion versus focal surgical tract injury.`,
    attached_files: [
      {
        id: 'file-eeg-1',
        name: 'ACNS_Guideline_11A_Intraoperative_EEG_Monitoring.pdf',
        size_kb: 430,
        role: 'Clinical Guideline / Literature',
        notes: 'American Clinical Neurophysiology Society (ACNS) guidelines on intraoperative EEG for cerebral perfusion and cortical monitoring.',
      },
    ],
  },
  {
    id: 'tpl-medicare-ma-repeat-baseline',
    name: 'Medicare / MA — LCD A56722 / A56773 & Repeat Baseline (-76) Corrected Claim / Appeal',
    description:
      'Handles Medicare & Medicare Advantage (UHC/AARP, HealthSpring, WellMed, Humana MA) claims where 95938 (-76) base-kill edits (CO-273 / CO-151) or LCD A56722/A56773 crosswalk denials occurred.',
    payer_match: 'MEDICARE / MA',
    procedure_type: 'All Procedures',
    denial_scenario: 'Repeat Baseline (-76)',
    claim_type: 'pro',
    cpt_codes: ['95938', '95939', '95940', '95941', 'G0453'],
    is_base_template: false,
    is_active: true,
    updated_at: '2026-10-26',
    template_text: `MEDICARE / MA LCD A56722 & MUE / MODIFIER -76 APPEAL (Claim {{CLAIM_NUMBER}}):
- Payer & Plan: {{PAYER_NAME}} | Claim {{CLAIM_NUMBER}} (ICN: {{ICN}}) | DOS: {{DOS}}.
- CMS Billing & Coding Article Alignment (A56722 / A56773): Patient's documented diagnoses ({{ALL_DX}}, Primary: {{PRIMARY_DX}}) are explicitly listed in the ICD-10-CM covered group for CPT(s) {{DENIED_CPTS}}.
- Resolution of Duplicate / MUE Line Edit (if CO-151 / CO-273 on 95938): CPT 95938 carries a CMS Medically Unlikely Edit (MUE) of 1 unit per date of service (MAI = 2). Request adjudication of the primary 1-unit 95938-26 four-limb SSEP service line (or process attached Frequency Code 7 corrected claim consolidating 95938-26 to 1 unit and G0453 continuous monitoring).`,
    attached_files: [
      {
        id: 'file-cms-1',
        name: 'CMS_Billing_Coding_Article_A56722_A56773_Crosswalk.pdf',
        size_kb: 675,
        role: 'Payer Medical Policy',
        notes: 'CMS MCD Articles A56722 & A56773 covered CPT and ICD-10 crosswalk tables for IONM and G0453.',
      },
    ],
  },
  {
    id: 'tpl-ent-cranial-nerve',
    name: 'ENT Thyroid / Parotid RLN & Cranial Nerve EMG (95867 / 95868) Medical Necessity',
    description:
      'Specialized clinical template for thyroidectomy, parathyroidectomy, parotidectomy, mastoidectomy, and Inspire hypoglossal nerve procedures utilizing cranial nerve EMG (95867/95868).',
    payer_match: 'All Payers',
    procedure_type: 'ENT / Thyroid / Parotid',
    denial_scenario: 'Medical Necessity / Experimental',
    claim_type: 'pro',
    cpt_codes: ['95867', '95868', '95860', '95940', '95941'],
    is_base_template: false,
    is_active: true,
    updated_at: '2026-10-23',
    template_text: `ENT / CRANIAL NERVE EMG (95867 / 95868) CLINICAL APPEAL (Claim {{CLAIM_NUMBER}}):
- Surgical Procedure: {{PROCEDURE_TYPE}} at {{FACILITY_NAME}} on {{DOS}} (Diagnoses: {{ALL_DX}}).
- Recurrent Laryngeal / Facial / Cranial Nerve Preservation: Continuous spontaneous and triggered cranial nerve EMG ({{DENIED_CPTS}}) was ordered by the surgeon and monitored by {{RENDERING_PROVIDER}} to identify, map, and preserve the recurrent laryngeal nerve (CN X) / facial nerve (CN VII) / hypoglossal nerve (CN XII) during dissection, preventing permanent vocal cord paralysis, airway compromise, or facial nerve palsy.`,
    attached_files: [
      {
        id: 'file-ent-1',
        name: 'AAO_HNS_Clinical_Practice_Guideline_Thyroid_Parotid_IONM.pdf',
        size_kb: 510,
        role: 'Clinical Guideline / Literature',
        notes: 'American Academy of Otolaryngology-Head and Neck Surgery guidelines on RLN and facial nerve EMG monitoring.',
      },
    ],
  },
];

/**
 * Loads appeal templates from localStorage, falling back to DEFAULT_APPEAL_TEMPLATES.
 */
export const loadAppealTemplates = () => {
  try {
    const raw = window.localStorage.getItem(TEMPLATES_STORAGE_KEY);
    if (!raw) return DEFAULT_APPEAL_TEMPLATES;
    const parsed = JSON.parse(raw);
    if (Array.isArray(parsed) && parsed.length > 0) {
      return parsed;
    }
  } catch (err) {
    console.warn('Could not read appeal templates from localStorage:', err);
  }
  return DEFAULT_APPEAL_TEMPLATES;
};

/**
 * Persists appeal templates array to localStorage.
 */
export const saveAppealTemplates = (templates) => {
  try {
    window.localStorage.setItem(TEMPLATES_STORAGE_KEY, JSON.stringify(templates));
  } catch (err) {
    console.warn('Could not save appeal templates to localStorage:', err);
  }
};

/**
 * Resets appeal templates in localStorage back to DEFAULT_APPEAL_TEMPLATES.
 */
export const resetAppealTemplatesToDefault = () => {
  try {
    window.localStorage.removeItem(TEMPLATES_STORAGE_KEY);
  } catch (err) {
    console.warn('Could not reset appeal templates in localStorage:', err);
  }
  return DEFAULT_APPEAL_TEMPLATES;
};

/**
 * Loads per-claim template selection history from localStorage.
 */
export const loadClaimAppealSelections = () => {
  try {
    const raw = window.localStorage.getItem(CLAIM_SELECTIONS_STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch (err) {
    return {};
  }
};

/**
 * Saves the selected templates, procedure type, and submission channel for a specific claim.
 */
export const saveClaimAppealSelection = (claimNumber, selectionObj) => {
  if (!claimNumber) return {};
  const existing = loadClaimAppealSelections();
  const updated = {
    ...existing,
    [String(claimNumber).toUpperCase()]: {
      ...selectionObj,
      saved_at: new Date().toISOString(),
    },
  };
  try {
    window.localStorage.setItem(CLAIM_SELECTIONS_STORAGE_KEY, JSON.stringify(updated));
  } catch (err) {
    console.warn('Could not save claim appeal selection:', err);
  }
  return updated;
};

/**
 * Deterministically infers the surgical Procedure Type & anatomical region from the claim's
 * ICD-10 diagnosis codes (`billed_icd10_codes` + `revedy_icd10_codes`) and CPT codes.
 */
export const inferClaimProcedureType = (claim) => {
  if (!claim) {
    return {
      procedureType: 'Thoracic / Lumbar Spine (TLIF / ALIF / Decompression)',
      shortLabel: 'Spine IONM',
      colorScheme: 'blue',
      evidenceCodes: [],
      reason: 'Default IONM surgical profile',
    };
  }

  const allIcds = [
    ...(claim.billed_icd10_codes || []),
    ...(claim.revedy_icd10_codes || []),
  ]
    .map((c) => String(c || '').trim().toUpperCase())
    .filter(Boolean);

  const uniqueIcds = Array.from(new Set(allIcds));
  const allCpts = [
    ...(claim.denied_core_cpts || []),
    ...(claim.denied_cpts || []),
    ...(claim.paid_cpts || []),
  ]
    .map((c) => String(c || '').trim().toUpperCase())
    .filter(Boolean);

  // 1. ENT / Thyroid / Parotid / Cranial Nerve (CPTs 95867/95868 or ENT ICD-10s)
  const entCpts = allCpts.filter((c) => c === '95867' || c === '95868');
  const entIcds = uniqueIcds.filter((code) =>
    /^(E0[4-7]|E21|C73|D11|C07|D34|D35|H6\d|H7\d|J38|K11|G47\.33)/.test(code)
  );
  if (entCpts.length > 0 || entIcds.length > 0) {
    const ev = [...entCpts, ...entIcds].slice(0, 3);
    return {
      procedureType: 'ENT / Thyroid / Parotid',
      shortLabel: 'ENT / Cranial Nerve',
      colorScheme: 'pink',
      evidenceCodes: ev,
      reason: `Detected from ${ev.join(', ')}`,
    };
  }

  // 2. Vascular / Carotid (CEA / TCAR / Aortic)
  const vascIcds = uniqueIcds.filter((code) => /^(I65\.2|I65\.8|I65\.9|I70|I71|I72|I77)/.test(code));
  if (vascIcds.length > 0) {
    return {
      procedureType: 'Vascular / Carotid (CEA / TCAR / Aortic)',
      shortLabel: 'Vascular / Carotid',
      colorScheme: 'red',
      evidenceCodes: vascIcds.slice(0, 3),
      reason: `Detected from vascular DX ${vascIcds.slice(0, 2).join(', ')}`,
    };
  }

  // 3. Craniotomy / Intracranial (Tumor / Aneurysm / MVD / TBI)
  const cranIcds = uniqueIcds.filter((code) =>
    /^(D32|D33|C71|C79\.3|D43|G93|G91|I60|I61|I62|I67\.1|S06|G50|G51|G52|Q28)/.test(code)
  );
  if (cranIcds.length > 0) {
    return {
      procedureType: 'Craniotomy / Intracranial',
      shortLabel: 'Craniotomy / Brain',
      colorScheme: 'purple',
      evidenceCodes: cranIcds.slice(0, 3),
      reason: `Detected from intracranial DX ${cranIcds.slice(0, 2).join(', ')}`,
    };
  }

  // 4. Scoliosis / Spinal Deformity
  const deformityIcds = uniqueIcds.filter((code) => /^(M41|M40|Q67\.5|Q76)/.test(code));
  if (deformityIcds.length > 0) {
    return {
      procedureType: 'Scoliosis / Spinal Deformity',
      shortLabel: 'Scoliosis / Deformity',
      colorScheme: 'orange',
      evidenceCodes: deformityIcds.slice(0, 3),
      reason: `Detected from deformity DX ${deformityIcds.slice(0, 2).join(', ')}`,
    };
  }

  // 5. Cervical Spine (M50.*, M47.*2, M48.02, M43.12, S12-S14)
  const cervIcds = uniqueIcds.filter((code) =>
    /^(M50|M47\.[0128]1?[123]|M48\.0[123]|M43\.1[123]|M54\.1[23]|S12|S13|S14|G83\.4)/.test(code)
  );
  if (cervIcds.length > 0) {
    return {
      procedureType: 'Cervical Spine (ACDF / Posterior)',
      shortLabel: 'Cervical Spine',
      colorScheme: 'cyan',
      evidenceCodes: cervIcds.slice(0, 3),
      reason: `Detected from cervical DX ${cervIcds.slice(0, 2).join(', ')}`,
    };
  }

  // 6. Thoracic / Lumbar Spine (M51.*, M47.*6/7, M48.06*, M43.16/17, 51785, etc.)
  const lumbarIcds = uniqueIcds.filter((code) =>
    /^(M51|M47|M48|M43|M53|M54|M46|M96|S22|S23|S24|S32|S33|S34)/.test(code)
  );
  if (lumbarIcds.length > 0 || allCpts.includes('51785') || allCpts.includes('95861') || allCpts.includes('95938')) {
    const ev = (lumbarIcds.length > 0 ? lumbarIcds : allCpts).slice(0, 3);
    return {
      procedureType: 'Thoracic / Lumbar Spine (TLIF / ALIF / Decompression)',
      shortLabel: 'Thoracic / Lumbar Spine',
      colorScheme: 'blue',
      evidenceCodes: ev,
      reason:
        lumbarIcds.length > 0
          ? `Detected from thoracolumbar DX ${lumbarIcds.slice(0, 2).join(', ')}`
          : `Detected from spine IONM CPTs (${ev.join(', ')})`,
    };
  }

  return {
    procedureType: 'Thoracic / Lumbar Spine (TLIF / ALIF / Decompression)',
    shortLabel: 'Spine / General IONM',
    colorScheme: 'gray',
    evidenceCodes: uniqueIcds.slice(0, 2),
    reason: 'Inferred from IONM claim profile',
  };
};

/**
 * Helper to check if a claim's payer_name matches a template's payer_match tag.
 */
const doesPayerMatch = (claimPayerName, templatePayerMatch) => {
  if (!templatePayerMatch || templatePayerMatch === 'All Payers') return { matches: true, isSpecific: false };
  const p = String(claimPayerName || '').toUpperCase();
  const target = String(templatePayerMatch || '').toUpperCase();

  if (target === 'CIGNA' && (p.includes('CIGNA') || p.includes('ALLEGIANCE'))) {
    return { matches: true, isSpecific: true, label: 'Payer: Cigna' };
  }
  if (target === 'AETNA' && p.includes('AETNA') && !p.includes('MEDICARE')) {
    return { matches: true, isSpecific: true, label: 'Payer: Aetna' };
  }
  if (
    target === 'UNITED HEALTHCARE / UHC' &&
    (p.includes('UNITED HEALTH') || p.includes('UHC') || p.includes('OPTUM') || p.includes('UMR') || p.includes('SUREST'))
  ) {
    return { matches: true, isSpecific: true, label: 'Payer: UHC / Optum' };
  }
  if (target === 'UMR' && p.includes('UMR')) {
    return { matches: true, isSpecific: true, label: 'Payer: UMR' };
  }
  if (target === 'BCBS' && (p.includes('BCBS') || p.includes('BLUE') || p.includes('ANTHEM') || p.includes('PREMERA') || p.includes('REGENCE'))) {
    return { matches: true, isSpecific: true, label: 'Payer: BCBS / Anthem' };
  }
  if (
    target === 'MEDICARE / MA' &&
    (p.includes('MEDICARE') ||
      p.includes('HEALTHSPRING') ||
      p.includes('WELLMED') ||
      p.includes('AARP') ||
      p.includes('HUMANA') ||
      p.includes('NOVITAS') ||
      p.includes('NORIDIAN') ||
      p.includes('PALMETTO'))
  ) {
    return { matches: true, isSpecific: true, label: 'Payer: Medicare / MA' };
  }
  if (target === 'HUMANA' && p.includes('HUMANA')) {
    return { matches: true, isSpecific: true, label: 'Payer: Humana' };
  }
  if (p.includes(target)) {
    return { matches: true, isSpecific: true, label: `Payer: ${templatePayerMatch}` };
  }
  return { matches: false, isSpecific: false };
};

/**
 * Scores and selects the best-matching templates for a given claim.
 * Returns { inferredProcedure, matchedTemplates, otherTemplates, recommendedChannel }.
 */
export const matchTemplatesForClaim = (claim, allTemplates = [], overrideProcedureType = '') => {
  const activeTemplates = (allTemplates || []).filter((t) => t.is_active !== false);
  const inferred = inferClaimProcedureType(claim);
  const effectiveProcedure = overrideProcedureType || inferred.procedureType;

  const deniedCpts = (
    claim?.denied_core_cpts && claim.denied_core_cpts.length > 0
      ? claim.denied_core_cpts
      : claim?.denied_cpts || []
  ).map((c) => String(c || '').toUpperCase());

  const topCarc = String(claim?.top_carc || '').toUpperCase();
  const topRarc = String(claim?.top_rarc || '').toUpperCase();
  const reasonText = String(claim?.primary_denial_reason || '').toUpperCase();
  const hasDenialLetter = Boolean(claim?.recent_denial_doc?.has_denial_letter);

  // Recommend submission channel based on claim pattern
  let recommendedChannel = 'STANDARD_APPEAL_PACKET';
  if (
    topCarc.includes('273') ||
    topCarc.includes('151') ||
    reasonText.includes('MUE') ||
    reasonText.includes('FREQUENCY')
  ) {
    recommendedChannel = 'CORRECTED_CLAIM_FREQ_7';
  } else if (
    !hasDenialLetter &&
    (String(claim?.payer_name || '').toUpperCase().includes('UHC') ||
      String(claim?.payer_name || '').toUpperCase().includes('AETNA') ||
      String(claim?.payer_name || '').toUpperCase().includes('BCBS'))
  ) {
    recommendedChannel = 'PAYER_PORTAL_EAPPEAL';
  }

  const matched = [];
  const others = [];

  activeTemplates.forEach((tpl) => {
    const reasons = [];
    let score = 0;

    if (tpl.is_base_template) {
      reasons.push('Base NMA Letterhead & Structure');
      score += 100;
      matched.push({ ...tpl, matchReasons: reasons, matchScore: score });
      return;
    }

    const payerCheck = doesPayerMatch(claim?.payer_name, tpl.payer_match);
    // If template specifies a specific payer and the claim is for a different payer, put in `others`
    if (tpl.payer_match && tpl.payer_match !== 'All Payers' && !payerCheck.matches) {
      others.push({ ...tpl, matchReasons: [], matchScore: 0 });
      return;
    }

    if (payerCheck.isSpecific) {
      score += 45;
      reasons.push(payerCheck.label);
    }

    // Check Procedure Type match
    const tplProc = tpl.procedure_type || 'All Procedures';
    const isProcMatch =
      tplProc === 'All Procedures' ||
      tplProc === effectiveProcedure ||
      (tplProc.includes('Spine') && effectiveProcedure.includes('Spine'));

    if (tplProc !== 'All Procedures') {
      if (tplProc === effectiveProcedure) {
        score += 35;
        reasons.push(`Procedure: ${tplProc.split(' (')[0]}`);
      } else if (tplProc.includes('Spine') && effectiveProcedure.includes('Spine') && payerCheck.isSpecific) {
        score += 15;
        reasons.push('Procedure: Spine IONM');
      } else {
        // Specific procedure template (e.g. ENT or Craniotomy) that doesn't match this claim's procedure
        // Exception: EEG template (95822/95955) matches whenever 95822/95955 is denied!
        const isEegTemplateWithDeniedEeg =
          (tpl.cpt_codes || []).some((c) => c === '95822' || c === '95955') &&
          deniedCpts.some((c) => c === '95822' || c === '95955');
        if (!isEegTemplateWithDeniedEeg) {
          others.push({ ...tpl, matchReasons: [], matchScore: 0 });
          return;
        }
      }
    }

    // Check Denied CPT overlap
    const tplCpts = (tpl.cpt_codes || []).map((c) => String(c).toUpperCase());
    const overlappingCpts = deniedCpts.filter((c) => tplCpts.includes(c));
    if (overlappingCpts.length > 0) {
      score += 25 + overlappingCpts.length * 5;
      reasons.push(`Denied CPT: ${overlappingCpts.slice(0, 3).join(', ')}`);
    } else if (tplCpts.length > 0 && tpl.denial_scenario === 'EEG Modality (95822 vs 95955)') {
      // Don't auto-pick the EEG template if neither 95822 nor 95955 was denied
      others.push({ ...tpl, matchReasons: [], matchScore: 0 });
      return;
    }

    // Check CARC / RARC / Denial Scenario signals
    if (
      tpl.denial_scenario === 'Repeat Baseline (-76)' &&
      (topCarc.includes('273') || topCarc.includes('151') || reasonText.includes('76') || deniedCpts.includes('95938'))
    ) {
      score += 20;
      reasons.push(topCarc ? `CARC: ${topCarc}` : 'Baseline SSEP (95938)');
    } else if (
      tpl.denial_scenario === 'CPT-DX Crosswalk (Policy 0509)' &&
      (topRarc.includes('M76') || topRarc.includes('MA63') || reasonText.includes('0509') || reasonText.includes('181'))
    ) {
      score += 25;
      reasons.push(`Policy 0509 / ${topCarc || 'Crosswalk'}`);
    } else if (
      tpl.denial_scenario === 'Post-Record Review / Clinical Doc' &&
      (hasDenialLetter || topCarc.includes('252') || topCarc.includes('150') || reasonText.includes('RECORD'))
    ) {
      score += 20;
      reasons.push(hasDenialLetter ? 'Lockbox Denial Letter' : 'Post-Review Denial');
    }

    if (score >= 45 && isProcMatch) {
      matched.push({ ...tpl, matchReasons: reasons, matchScore: score });
    } else if (score >= 50) {
      matched.push({ ...tpl, matchReasons: reasons, matchScore: score });
    } else {
      others.push({ ...tpl, matchReasons: reasons, matchScore: score });
    }
  });

  matched.sort((a, b) => {
    if (a.is_base_template !== b.is_base_template) return a.is_base_template ? 1 : -1;
    return b.matchScore - a.matchScore;
  });

  // If only the base template matched, include the highest-scoring non-base template if available
  const nonBaseMatched = matched.filter((m) => !m.is_base_template);
  if (nonBaseMatched.length === 0 && others.length > 0) {
    others.sort((a, b) => b.matchScore - a.matchScore);
    const fallback = others.shift();
    if (fallback) {
      matched.unshift({
        ...fallback,
        matchReasons: fallback.matchReasons.length ? fallback.matchReasons : ['General IONM Clinical Template'],
        matchScore: 40,
      });
    }
  }

  return {
    inferredProcedure: inferred,
    effectiveProcedure,
    recommendedChannel,
    matchedTemplates: matched,
    otherTemplates: others,
  };
};

/**
 * Replaces {{PLACEHOLDER}} tokens in template text with values from the target claim.
 */
export const hydrateTemplateText = (
  templateText,
  claim,
  procedureType = '',
  selectedPolicies = []
) => {
  if (!templateText) return '';
  if (!claim) return templateText;

  const deniedCpts =
    claim.denied_core_cpts && claim.denied_core_cpts.length > 0
      ? claim.denied_core_cpts.join(', ')
      : (claim.denied_cpts || []).join(', ') || 'See Line Items';
  const paidCpts = (claim.paid_cpts || []).length > 0 ? claim.paid_cpts.join(', ') : 'None (Zero-Pay)';
  const billedIcds = (claim.billed_icd10_codes || []).length
    ? claim.billed_icd10_codes
    : claim.revedy_icd10_codes || [];
  const primaryDx = billedIcds[0] || 'See Operative Note';
  const allDx = billedIcds.length > 0 ? billedIcds.join(', ') : 'See Operative Note';
  const topCarc = claim.top_carc
    ? `${claim.top_carc}${claim.top_rarc ? ` / ${claim.top_rarc}` : ''}`
    : 'N/A';

  const selectedPolicyCitationsStr =
    Array.isArray(selectedPolicies) && selectedPolicies.length > 0
      ? selectedPolicies
          .map((p) => {
            const ref = p.policy_reference_number || `DOC-${p.doc_id}`;
            const eff = p.published_effective_date ? ` (Eff: ${p.published_effective_date})` : '';
            return `[${ref}] ${p.title}${eff}`;
          })
          .join('; ')
      : 'Applicable Payer Medical & Payment Coverage Policies';

  const replacements = {
    '{{CLAIM_NUMBER}}': claim.claim_number || 'N/A',
    '{{CASE_NUMBER}}': claim.nma_case_no || 'N/A',
    '{{PATIENT_NAME}}': claim.patient_name || 'Patient on File',
    '{{DOS}}': claim.date_of_service || 'N/A',
    '{{PAYER_NAME}}': claim.payer_name || 'Payer',
    '{{ICN}}': claim.payer_claim_control_number || 'Pending / See EOB',
    '{{FACILITY_NAME}}': claim.facility_name || 'Surgical Facility',
    '{{RENDERING_PROVIDER}}': claim.rendering_provider || 'NMA Monitoring Neurologist',
    '{{POS}}': claim.place_of_service || '21/22',
    '{{PROCEDURE_TYPE}}': procedureType || inferClaimProcedureType(claim).procedureType,
    '{{DENIED_CPTS}}': deniedCpts,
    '{{PAID_CPTS}}': paidCpts,
    '{{TOP_CARC}}': topCarc,
    '{{PRIMARY_DX}}': primaryDx,
    '{{ALL_DX}}': allDx,
    '{{DENIAL_REASON}}': claim.primary_denial_reason || 'Denied per remittance',
    '{{SELECTED_POLICY_CITATIONS}}': selectedPolicyCitationsStr,
  };

  let result = String(templateText);
  Object.entries(replacements).forEach(([token, val]) => {
    result = result.split(token).join(String(val));
  });
  return result;
};

/**
 * Compiles selected templates, selected payer policies, attached files, procedure type,
 * submission channel, and inline-edited template text into the `custom_instructions`
 * string sent to POST /api/nma/appeals/jobs.
 */
export const buildAppealJobCustomInstructions = ({
  selectedTemplates = [],
  selectedPolicies = [],
  resolvedAuthority = null,
  procedureType = '',
  submissionChannel = 'STANDARD_APPEAL_PACKET',
  compiledTemplateText = '',
  extraFiles = [],
  billerNotes = '',
}) => {
  const channelObj =
    SUBMISSION_CHANNEL_OPTIONS.find((c) => c.value === submissionChannel) || SUBMISSION_CHANNEL_OPTIONS[0];
  const tplNames = selectedTemplates.map((t) => t.name).join(' + ');
  const allFiles = [
    ...selectedTemplates.flatMap((t) => (t.attached_files || []).map((f) => `${f.name} (${f.role})`)),
    ...(extraFiles || []).map((f) => `${f.name} (${f.role || 'Case Supplement'})`),
  ];

  const sections = [
    `[APPEAL TEMPLATE & WORKFLOW CONFIGURATION]`,
    `- Submission Channel: ${channelObj.label}`,
    `- Classified Procedure Type: ${procedureType || 'Standard IONM'}`,
    `- Active Templates Applied (${selectedTemplates.length}): ${tplNames || 'Custom Override'}`,
  ];

  if (resolvedAuthority?.display_name) {
    sections.push(
      `- Resolved Canonical Payer Authority: ${resolvedAuthority.display_name} (${resolvedAuthority.authority_key || 'N/A'} | Plan State: ${resolvedAuthority.effective_plan_state || 'ALL'})`
    );
  }
  if (Array.isArray(selectedPolicies) && selectedPolicies.length > 0) {
    const polSummary = selectedPolicies
      .map(
        (p) =>
          `[${p.policy_reference_number || `DOC-${p.doc_id}`}] ${p.title}${
            p.published_effective_date ? ` (Eff: ${p.published_effective_date})` : ''
          }`
      )
      .join('; ');
    sections.push(`- Selected Payer Policies to Cite (${selectedPolicies.length}): ${polSummary}`);
  }
  if (allFiles.length > 0) {
    sections.push(`- Reference Policy & Sample Files Attached: ${allFiles.join('; ')}`);
  }
  if (billerNotes && billerNotes.trim()) {
    sections.push(`- Biller Specific Instructions: ${billerNotes.trim()}`);
  }
  if (compiledTemplateText && compiledTemplateText.trim()) {
    sections.push(`\n[MERGED TEMPLATE GUIDANCE & REQUIRED ARGUMENT STRUCTURE]\n${compiledTemplateText.trim()}`);
  }

  return sections.join('\n');
};
